import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { validateInvite, forgotPassword, resetPassword, verifyResetToken } from '../services/auth';
import { AlertCircle, ArrowLeft, CheckCircle, Eye, EyeOff, Gift, KeyRound, LogIn, Rocket, Shield, TrendingUp, UserPlus, Zap } from 'lucide-react';

// Particle Grid Background — Futuristic red matrix
const ParticleGridBackground = () => {
  return (
    <>
      <style>{`
        @keyframes gridPulse {
          0%, 100% { opacity: 0.03; }
          50% { opacity: 0.06; }
        }
        @keyframes particleFloat {
          0% { transform: translateY(0) scale(1); opacity: 0.6; }
          50% { transform: translateY(-8px) scale(1.2); opacity: 1; }
          100% { transform: translateY(0) scale(1); opacity: 0.6; }
        }
        @keyframes scanLine {
          0% { top: -2px; }
          100% { top: 100%; }
        }
        @keyframes countUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      <div style={{
        position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
        zIndex: 0, overflow: 'hidden', background: '#000',
      }}>
        {/* Grid pattern */}
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: `
            linear-gradient(rgba(224, 30, 58, 0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(224, 30, 58, 0.04) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
          animation: 'gridPulse 4s ease-in-out infinite',
        }} />

        {/* Radial glow - top */}
        <div style={{
          position: 'absolute', top: '-20%', left: '50%', transform: 'translateX(-50%)',
          width: '600px', height: '600px', borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(224, 30, 58, 0.12) 0%, transparent 70%)',
          filter: 'blur(60px)',
        }} />

        {/* Radial glow - bottom */}
        <div style={{
          position: 'absolute', bottom: '-30%', left: '30%',
          width: '500px', height: '500px', borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(139, 0, 0, 0.08) 0%, transparent 70%)',
          filter: 'blur(80px)',
        }} />

        {/* Floating particles */}
        {[...Array(12)].map((_, i) => (
          <div key={i} style={{
            position: 'absolute',
            width: `${3 + (i % 3) * 2}px`,
            height: `${3 + (i % 3) * 2}px`,
            background: '#E01E3A',
            borderRadius: '50%',
            left: `${8 + (i * 7.5)}%`,
            top: `${10 + ((i * 17) % 80)}%`,
            opacity: 0.4,
            boxShadow: '0 0 6px rgba(224, 30, 58, 0.5)',
            animation: `particleFloat ${2.5 + (i % 4) * 0.8}s ease-in-out infinite`,
            animationDelay: `${i * 0.3}s`,
          }} />
        ))}

        {/* Scan line */}
        <div style={{
          position: 'absolute', left: 0, right: 0,
          height: '1px',
          background: 'linear-gradient(90deg, transparent, rgba(224, 30, 58, 0.15), transparent)',
          animation: 'scanLine 6s linear infinite',
        }} />
      </div>
    </>
  );
};

const AuthPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isBM = language === 'bm' || language === 'ms';
  
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Forgot/Reset Password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetToken, setResetToken] = useState(null);
  const [resetEmail, setResetEmail] = useState('');
  
  // Invite system state
  const [inviteCode, setInviteCode] = useState(null);
  const [inviteData, setInviteData] = useState(null);
  const [inviteError, setInviteError] = useState('');
  const [validatingInvite, setValidatingInvite] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    phone: '',
    newPassword: '',
    confirmPassword: ''
  });

  const { login, register, registerWithInvite } = useAuth();

  // Check for invite code or reset token in URL on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const invite = urlParams.get('invite');
    const token = urlParams.get('token');
    
    if (invite) {
      setInviteCode(invite);
      setIsLogin(false); // Switch to register mode
      validateInviteCode(invite);
    }
    
    // Check for password reset token
    if (token && window.location.pathname.includes('reset-password')) {
      setResetToken(token);
      setShowResetPassword(true);
      verifyResetTokenHandler(token);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateInviteCode = async (code) => {
    setValidatingInvite(true);
    setInviteError('');
    try {
      const result = await validateInvite(code);
      if (result.valid) {
        setInviteData(result);
      } else {
        setInviteError(isBM ? 'Invite link is invalid or has expired' : 'Invite link is invalid or has expired');
        setInviteCode(null);
      }
    } catch (err) {
      const message = err.response?.data?.detail || (isBM ? 'Invalid invite link' : 'Invalid invite link');
      setInviteError(message);
      setInviteCode(null);
    } finally {
      setValidatingInvite(false);
    }
  };

  const verifyResetTokenHandler = async (token) => {
    try {
      const result = await verifyResetToken(token);
      if (result.valid) {
        setResetEmail(result.email);
      }
    } catch (err) {
      setError(isBM ? 'Reset link is invalid or has expired. Please request a new link.' : 'Reset link is invalid or has expired. Please request a new link.');
      setShowResetPassword(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const result = await forgotPassword(formData.email);
      setSuccess(result.message || (isBM ? 'If email is registered, you will receive a password reset link.' : 'If email is registered, you will receive a password reset link.'));
      setFormData({ ...formData, email: '' });
    } catch (err) {
      setError(err.response?.data?.detail || (isBM ? 'An error occurred. Please try again.' : 'An error occurred. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate passwords match
    if (formData.newPassword !== formData.confirmPassword) {
      setError(isBM ? 'Passwords do not match' : 'Passwords do not match');
      return;
    }

    // Validate password strength
    const passwordErrors = [];
    if (formData.newPassword.length < 8) passwordErrors.push(isBM ? 'minimum 8 characters' : 'minimum 8 characters');
    if (!/[A-Z]/.test(formData.newPassword)) passwordErrors.push(isBM ? '1 uppercase letter' : '1 uppercase letter');
    if (!/[a-z]/.test(formData.newPassword)) passwordErrors.push(isBM ? '1 lowercase letter' : '1 lowercase letter');
    if (!/[0-9]/.test(formData.newPassword)) passwordErrors.push(isBM ? '1 number' : '1 number');
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(formData.newPassword)) passwordErrors.push(isBM ? '1 symbol' : '1 symbol');

    if (passwordErrors.length > 0) {
      setError((isBM ? 'Password must contain: ' : 'Password must contain: ') + passwordErrors.join(', '));
      return;
    }

    setLoading(true);

    try {
      const result = await resetPassword(resetToken, formData.newPassword);
      setSuccess(result.message || (isBM ? 'Password successfully updated!' : 'Password successfully updated!'));
      // Redirect to login after 2 seconds
      setTimeout(() => {
        setShowResetPassword(false);
        setResetToken(null);
        setIsLogin(true);
        window.history.pushState({}, '', '/');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || (isBM ? 'An error occurred. Please try again.' : 'An error occurred. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        const result = await login(formData.email, formData.password);
        if (!result.success) {
          setError(result.error);
        } else {
          // Redirect to dashboard on successful login
          navigate('/dashboard');
        }
      } else {
        // Password strength validation
        const passwordErrors = [];
        if (formData.password.length < 8) {
          passwordErrors.push(isBM ? 'minimum 8 characters' : 'minimum 8 characters');
        }
        if (!/[A-Z]/.test(formData.password)) {
          passwordErrors.push(isBM ? '1 uppercase letter' : '1 uppercase letter');
        }
        if (!/[a-z]/.test(formData.password)) {
          passwordErrors.push(isBM ? '1 lowercase letter' : '1 lowercase letter');
        }
        if (!/[0-9]/.test(formData.password)) {
          passwordErrors.push(isBM ? '1 number' : '1 number');
        }
        if (!/[!@#$%^&*(),.?":{}|<>]/.test(formData.password)) {
          passwordErrors.push(isBM ? '1 symbol (!@#$%^&*)' : '1 symbol (!@#$%^&*)');
        }
        
        if (passwordErrors.length > 0) {
          setError((isBM ? 'Password must contain: ' : 'Password must contain: ') + passwordErrors.join(', '));
          setLoading(false);
          return;
        }
        
        // Registration - INVITE ONLY
        if (!inviteCode || !inviteData) {
          setError(isBM ? 'Invite link required to register. Please get an invite from an existing user.' : 'Invite link required to register. Please get an invite from an existing user.');
          setLoading(false);
          return;
        }
        
        // Register with invite code
        const result = await registerWithInvite(
          inviteCode, 
          formData.email, 
          formData.password, 
          formData.name, 
          formData.phone || null
        );
        if (!result.success) {
          setError(result.error);
        } else {
          // Redirect to dashboard on successful registration
          navigate('/dashboard');
        }
      }
    } catch (err) {
      // Catch any remaining unhandled errors with user-friendly messages
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        setError('Server is taking too long to respond. Please try again.');
      } else if (err.code === 'ERR_NETWORK' || err.message?.includes('Network Error')) {
        setError('Unable to connect to server. Please check your internet connection.');
      } else if (err.response?.status === 429) {
        setError('Too many attempts. Please wait a moment and try again.');
      } else {
        setError('An error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: TrendingUp, text: 'Institutional Signals' },
    { icon: Shield, text: 'Bank-Grade Encryption' },
    { icon: Zap, text: 'Volume Profile Analysis' }
  ];

  // MSE SUPREME AI Red Theme (matching the app)
  const colors = {
    bgPrimary: '#000000',           // Black background
    bgCard: '#1A1A1A',              // Dark card background
    bgInput: '#2A2A2A',             // Input background
    accent: '#E01E3A',              // Crimson Red accent
    accentLight: '#FF4D6A',         // Light red
    accentGradient: 'linear-gradient(135deg, #E01E3A, #FF4D6A)', // Red gradient
    textPrimary: '#FFFFFF',         // White text
    textSecondary: '#E0E0E0',       // Light gray text
    textMuted: '#9E9E9E',           // Muted text
    border: '#333333',              // Border color
    borderFocus: '#E01E3A',         // Focus border (red)
    error: '#FF5252',               // Error red
    success: '#69F0AE',             // Success green
    invite: '#3B82F6',              // Invite blue
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#000000',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px',
      position: 'relative',
      overflow: 'hidden',
    }}>
      <ParticleGridBackground />
      <div style={{
        width: '100%',
        maxWidth: '500px',
        position: 'relative',
        zIndex: 1,
      }}>
        
        {/* MSE Logo + Branding */}
        <div style={{
          textAlign: 'center',
          marginBottom: '24px',
        }}>
          {/* Logo with glow */}
          <div style={{
            width: '80px', height: '80px',
            margin: '0 auto 16px',
            borderRadius: '20px',
            overflow: 'hidden',
            boxShadow: '0 0 40px rgba(224, 30, 58, 0.4), 0 0 80px rgba(224, 30, 58, 0.15)',
            border: '2px solid rgba(224, 30, 58, 0.3)',
          }}>
            <img 
              src="https://customer-assets.emergentagent.com/job_b546c7e0-8db7-4773-8c43-bb6a44e7d135/artifacts/0qixik5j_IMG_2358.jpeg"
              alt="MSE SUPREME AI"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </div>

          <h1 style={{
            fontSize: '1.8rem',
            fontWeight: '800',
            background: colors.accentGradient,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            marginBottom: '4px',
            letterSpacing: '1px',
          }}>
            MSE SUPREME AI
          </h1>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            marginBottom: '12px',
          }}>
            <div style={{ height: '1px', width: '40px', background: 'linear-gradient(to right, transparent, rgba(224,30,58,0.3))' }} />
            <span style={{
              fontSize: '0.6rem',
              fontWeight: '600',
              letterSpacing: '3px',
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.4)',
            }}>
              MULTI SETUP ENTRY
            </span>
            <div style={{ height: '1px', width: '40px', background: 'linear-gradient(to left, transparent, rgba(224,30,58,0.3))' }} />
          </div>
          
          {/* Power Tagline */}
          <p style={{ 
            color: '#E01E3A', 
            fontSize: '0.85rem', 
            fontWeight: '600',
            letterSpacing: '0.5px',
            marginBottom: '16px'
          }}>
            "Trade Like The Top 1%"
          </p>

          {/* Live Stats Counter */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '16px',
            marginBottom: '0',
          }}>
            {[
              { value: '500+', label: 'Active Traders' },
              { value: '50+', label: 'Data Sources' },
              { value: '24/7', label: 'AI Monitoring' },
            ].map((stat, idx) => (
              <div key={idx} style={{
                textAlign: 'center',
                animation: 'countUp 0.5s ease-out both',
                animationDelay: `${0.2 + idx * 0.15}s`,
              }}>
                <div style={{
                  fontSize: '16px',
                  fontWeight: '800',
                  color: '#E01E3A',
                  fontFamily: "'JetBrains Mono', monospace",
                  lineHeight: 1,
                }}>
                  {stat.value}
                </div>
                <div style={{
                  fontSize: '8px',
                  color: 'rgba(255,255,255,0.3)',
                  letterSpacing: '0.5px',
                  marginTop: '3px',
                  textTransform: 'uppercase',
                }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Invite Banner */}
        {validatingInvite && (
          <div style={{
            marginBottom: '20px',
            padding: '16px',
            background: `${colors.invite}15`,
            border: `1px solid ${colors.invite}40`,
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}>
            <div style={{
              width: '20px',
              height: '20px',
              border: '2px solid',
              borderColor: `${colors.invite} transparent ${colors.invite} transparent`,
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }} />
            <span style={{ color: colors.invite, fontSize: '0.9rem' }}>
              {isBM ? 'Verifying invite link...' : 'Verifying invite link...'}
            </span>
          </div>
        )}

        {inviteData && (
          <div style={{
            marginBottom: '20px',
            padding: '16px',
            background: `${colors.success}15`,
            border: `1px solid ${colors.success}40`,
            borderRadius: '12px',
          }} data-testid="invite-valid-banner">
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
              <Gift size={20} style={{ color: colors.success }} />
              <span style={{ color: colors.success, fontWeight: '600', fontSize: '0.95rem' }}>
                {isBM ? 'Valid Invite Link!' : 'Valid Invite Link!'}
              </span>
            </div>
            <p style={{ color: colors.textSecondary, fontSize: '0.85rem', margin: 0 }}>
              {isBM 
                ? <>Selamat datang, <strong>{inviteData.client_name}</strong>! Sila lengkapkan pendaftaran anda di bawah.</>
                : <>Welcome, <strong>{inviteData.client_name}</strong>! Please complete your registration below.</>}
            </p>
          </div>
        )}

        {inviteError && (
          <div style={{
            marginBottom: '20px',
            padding: '16px',
            background: `${colors.error}15`,
            border: `1px solid ${colors.error}40`,
            borderRadius: '12px',
          }} data-testid="invite-error-banner">
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <AlertCircle size={20} style={{ color: colors.error }} />
              <span style={{ color: colors.error, fontSize: '0.9rem' }}>
                {inviteError}
              </span>
            </div>
          </div>
        )}

        {/* Auth Card */}
        <div style={{
          background: colors.accentGradient,
          borderRadius: '24px',
          padding: '2px',
          boxShadow: '0 0 40px rgba(224, 30, 58, 0.15), 0 0 80px rgba(224, 30, 58, 0.05)',
        }}>
          <div style={{
            backgroundColor: 'rgba(20, 20, 20, 0.92)',
            backdropFilter: 'blur(20px)',
            borderRadius: '22px',
            padding: '32px',
          }}>
            
            {/* Welcome Message */}
            <div style={{
              textAlign: 'center',
              marginBottom: '24px',
            }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '8px',
              }}>
                <Rocket style={{ width: '24px', height: '24px', color: colors.accent }} />
                <span style={{ 
                  fontSize: '1.25rem', 
                  fontWeight: '600', 
                  color: colors.textPrimary 
                }}>
                  {inviteData 
                    ? (isBM ? 'Invite Registration' : 'Invite Registration')
                    : (isLogin 
                      ? (isBM ? 'Welcome Back!' : 'Welcome Back!') 
                      : (isBM ? 'Sertai Kami!' : 'Join Us!'))}
                </span>
              </div>
              <p style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                {inviteData 
                  ? (isBM ? 'Complete your information to register' : 'Complete your information to register your account')
                  : (isLogin 
                    ? (isBM ? 'Login to access your trading dashboard' : 'Login to access your trading dashboard')
                    : (isBM ? 'Create an account to start trading smart' : 'Register an account to start trading wisely'))}
              </p>
            </div>

            {/* Tab Switcher - Hide when invite is active */}
            {!inviteData && (
              <>
              <div style={{
                display: 'flex',
                gap: '8px',
                marginBottom: '24px',
                backgroundColor: colors.bgInput,
                padding: '4px',
                borderRadius: '12px',
              }}>
                <button
                  onClick={() => { setIsLogin(true); setError(''); }}
                  data-testid="login-tab"
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease',
                    border: 'none',
                    cursor: 'pointer',
                    background: isLogin ? colors.accentGradient : 'transparent',
                    color: isLogin ? '#FFFFFF' : colors.textMuted,
                    fontWeight: isLogin ? '600' : '400',
                    fontSize: '0.9375rem',
                  }}
                >
                  <LogIn style={{ width: '16px', height: '16px' }} />
                  {isBM ? 'Login' : 'Login'}
                </button>
                <button
                  onClick={() => { setIsLogin(false); setError(''); }}
                  data-testid="register-tab"
                  disabled={!inviteCode}
                  title={!inviteCode ? (isBM ? 'PM Admin to get invite link' : 'PM Admin to get invite link') : ''}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease',
                    border: 'none',
                    cursor: !inviteCode ? 'not-allowed' : 'pointer',
                    background: !isLogin ? colors.accentGradient : 'transparent',
                    color: !isLogin ? '#FFFFFF' : colors.textMuted,
                    fontWeight: !isLogin ? '600' : '400',
                    fontSize: '0.9375rem',
                    opacity: !inviteCode ? 0.5 : 1,
                  }}
                >
                  <UserPlus style={{ width: '16px', height: '16px' }} />
                  {isBM ? 'Register' : 'Register'}
                </button>
              </div>
              
              {/* Hint for disabled register button */}
              {!inviteCode && isLogin && (
                <div style={{
                  textAlign: 'center',
                  fontSize: '11px',
                  color: '#F59E0B',
                  marginBottom: '16px',
                  padding: '8px 12px',
                  background: 'rgba(245, 158, 11, 0.1)',
                  borderRadius: '8px'
                }}>
                  🔒 {isBM ? 'Registration requires invite link. PM Admin for access.' : 'Registration requires invite link. PM Admin for access.'}
                </div>
              )}
            </>
            )}

            {/* Invite-Only Notice */}
            {!isLogin && !inviteCode && (
              <div style={{
                marginBottom: '20px',
                padding: '16px',
                backgroundColor: 'rgba(224, 30, 58, 0.1)',
                border: '1px solid rgba(224, 30, 58, 0.3)',
                borderRadius: '12px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '24px', marginBottom: '8px' }}>🔒</div>
                <div style={{ color: colors.accent, fontWeight: '600', marginBottom: '8px' }}>
                  {isBM ? 'Pendaftaran Eksklusif' : 'Exclusive Registration'}
                </div>
                <div style={{ color: colors.textMuted, fontSize: '13px', lineHeight: '1.6' }}>
                  {isBM 
                    ? <>Pendaftaran hanya boleh dilakukan melalui <strong style={{ color: '#fff' }}>Invite Link</strong> yang disahkan oleh admin.</>
                    : <>Registration can only be done through <strong style={{ color: '#fff' }}>Invite Link</strong> verified by admin.</>}
                </div>
                <div style={{ 
                  marginTop: '12px', 
                  padding: '12px',
                  background: 'rgba(255,255,255,0.05)',
                  borderRadius: '8px',
                  fontSize: '12px', 
                  color: '#F59E0B',
                  lineHeight: '1.5'
                }}>
                  📩 <strong>{isBM ? 'Cara Mendaftar:' : 'How to Register:'}</strong><br />
                  {isBM ? 'PM Admin to get invite link and platform access.' : 'PM Admin to get invite link and platform access.'}
                </div>
                <div style={{ marginTop: '12px', fontSize: '12px', color: colors.textMuted }}>
                  {isBM ? 'Sudah ada akaun? ' : 'Already have an account? '}
                  <span 
                    onClick={() => setIsLogin(true)} 
                    style={{ color: colors.accent, cursor: 'pointer', textDecoration: 'underline' }}
                  >{isBM ? 'Login here' : 'Login here'}</span>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div style={{
                marginBottom: '20px',
                padding: '14px',
                backgroundColor: 'rgba(255, 82, 82, 0.1)',
                border: '1px solid rgba(255, 82, 82, 0.3)',
                borderRadius: '10px',
                color: colors.error,
                fontSize: '0.875rem',
              }} data-testid="auth-error">
                {error}
              </div>
            )}

            {/* Success Message */}
            {success && (
              <div style={{
                marginBottom: '20px',
                padding: '14px',
                backgroundColor: 'rgba(105, 240, 174, 0.1)',
                border: '1px solid rgba(105, 240, 174, 0.3)',
                borderRadius: '10px',
                color: colors.success,
                fontSize: '0.875rem',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
              }} data-testid="auth-success">
                <CheckCircle size={18} />
                {success}
              </div>
            )}

            {/* FORGOT PASSWORD FORM */}
            {showForgotPassword && !showResetPassword && (
              <div>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '10px', 
                  marginBottom: '20px',
                  cursor: 'pointer'
                }} onClick={() => { setShowForgotPassword(false); setError(''); setSuccess(''); }}>
                  <ArrowLeft size={18} style={{ color: colors.accent }} />
                  <span style={{ color: colors.accent, fontSize: '0.9rem' }}>{isBM ? 'Back to Login' : 'Back to Login'}</span>
                </div>
                
                <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                  <KeyRound size={40} style={{ color: colors.accent, marginBottom: '12px' }} />
                  <h3 style={{ color: colors.textPrimary, margin: '0 0 8px', fontSize: '1.25rem' }}>
                    {isBM ? 'Lupa Kata Laluan?' : 'Forgot Password?'}
                  </h3>
                  <p style={{ color: colors.textMuted, fontSize: '0.85rem', margin: 0 }}>
                    {isBM ? 'Masukkan email anda dan kami akan hantar link untuk reset kata laluan.' : 'Enter your email and we will send a password reset link.'}
                  </p>
                </div>

                <form onSubmit={handleForgotPassword} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                  <div>
                    <label style={{
                      display: 'block',
                      color: colors.textSecondary,
                      fontSize: '0.875rem',
                      marginBottom: '8px',
                      fontWeight: '500',
                    }}>Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        padding: '14px 16px',
                        backgroundColor: colors.bgInput,
                        border: `2px solid ${colors.border}`,
                        borderRadius: '12px',
                        color: colors.textPrimary,
                        fontSize: '1rem',
                        outline: 'none',
                        boxSizing: 'border-box',
                      }}
                      placeholder="email@example.com"
                      data-testid="forgot-email"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="forgot-submit"
                    style={{
                      width: '100%',
                      padding: '16px',
                      background: colors.accentGradient,
                      color: '#FFFFFF',
                      fontWeight: '600',
                      fontSize: '1rem',
                      borderRadius: '12px',
                      border: 'none',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      opacity: loading ? 0.7 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      boxShadow: '0 4px 15px rgba(224, 30, 58, 0.4)',
                    }}
                  >
                    {loading ? (isBM ? 'Menghantar...' : 'Sending...') : (isBM ? 'Send Reset Link' : 'Send Reset Link')}
                  </button>
                </form>
              </div>
            )}

            {/* RESET PASSWORD FORM */}
            {showResetPassword && (
              <div>
                <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                  <KeyRound size={40} style={{ color: colors.accent, marginBottom: '12px' }} />
                  <h3 style={{ color: colors.textPrimary, margin: '0 0 8px', fontSize: '1.25rem' }}>
                    {isBM ? 'Reset Password' : 'Reset Password'}
                  </h3>
                  <p style={{ color: colors.textMuted, fontSize: '0.85rem', margin: 0 }}>
                    {resetEmail && `Email: ${resetEmail}`}<br />
                    {isBM ? 'Masukkan kata laluan baru anda.' : 'Enter your new password.'}
                  </p>
                </div>

                <form onSubmit={handleResetPassword} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                  <div>
                    <label style={{
                      display: 'block',
                      color: colors.textSecondary,
                      fontSize: '0.875rem',
                      marginBottom: '8px',
                      fontWeight: '500',
                    }}>{isBM ? 'New Password' : 'New Password'}</label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={formData.newPassword}
                        onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                        required
                        style={{
                          width: '100%',
                          padding: '14px 50px 14px 16px',
                          backgroundColor: colors.bgInput,
                          border: `2px solid ${colors.border}`,
                          borderRadius: '12px',
                          color: colors.textPrimary,
                          fontSize: '1rem',
                          outline: 'none',
                          boxSizing: 'border-box',
                        }}
                        placeholder={isBM ? "Kata laluan baru" : "New password"}
                        data-testid="reset-new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        style={{
                          position: 'absolute',
                          right: '14px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          color: colors.textMuted,
                          cursor: 'pointer',
                          padding: '4px',
                        }}
                      >
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                    <div style={{ fontSize: '11px', color: colors.textMuted, marginTop: '6px' }}>
                      {isBM ? 'Min 8 aksara, 1 uppercase letter, 1 lowercase letter, 1 number, 1 symbol' : 'Min 8 chars, 1 uppercase, 1 lowercase, 1 number, 1 symbol'}
                    </div>
                  </div>

                  <div>
                    <label style={{
                      display: 'block',
                      color: colors.textSecondary,
                      fontSize: '0.875rem',
                      marginBottom: '8px',
                      fontWeight: '500',
                    }}>{isBM ? 'Confirm Password' : 'Confirm Password'}</label>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        padding: '14px 16px',
                        backgroundColor: colors.bgInput,
                        border: `2px solid ${colors.border}`,
                        borderRadius: '12px',
                        color: colors.textPrimary,
                        fontSize: '1rem',
                        outline: 'none',
                        boxSizing: 'border-box',
                      }}
                      placeholder={isBM ? "Sahkan kata laluan" : "Confirm password"}
                      data-testid="reset-confirm-password"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="reset-submit"
                    style={{
                      width: '100%',
                      padding: '16px',
                      background: colors.accentGradient,
                      color: '#FFFFFF',
                      fontWeight: '600',
                      fontSize: '1rem',
                      borderRadius: '12px',
                      border: 'none',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      opacity: loading ? 0.7 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      boxShadow: '0 4px 15px rgba(224, 30, 58, 0.4)',
                    }}
                  >
                    {loading ? (isBM ? 'Mengemaskini...' : 'Updating...') : (isBM ? 'Reset Password' : 'Reset Password')}
                  </button>
                </form>
              </div>
            )}

            {/* Form - Only show when not in forgot/reset mode */}
            {!showForgotPassword && !showResetPassword && (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
              {(!isLogin || inviteData) && (
                <div>
                  <label style={{
                    display: 'block',
                    color: colors.textSecondary,
                    fontSize: '0.875rem',
                    marginBottom: '8px',
                    fontWeight: '500',
                  }}>{isBM ? 'Nama' : 'Name'}</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      backgroundColor: colors.bgInput,
                      border: `2px solid ${colors.border}`,
                      borderRadius: '12px',
                      color: colors.textPrimary,
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'all 0.2s ease',
                      boxSizing: 'border-box',
                    }}
                    placeholder={isBM ? "Nama penuh anda" : "Your full name"}
                    required={!isLogin || !!inviteData}
                    data-testid="register-name"
                    onFocus={(e) => e.target.style.borderColor = colors.borderFocus}
                    onBlur={(e) => e.target.style.borderColor = colors.border}
                  />
                </div>
              )}

              <div>
                <label style={{
                  display: 'block',
                  color: colors.textSecondary,
                  fontSize: '0.875rem',
                  marginBottom: '8px',
                  fontWeight: '500',
                }}>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    backgroundColor: colors.bgInput,
                    border: `2px solid ${colors.border}`,
                    borderRadius: '12px',
                    color: colors.textPrimary,
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'all 0.2s ease',
                    boxSizing: 'border-box',
                  }}
                  placeholder="email@example.com"
                  required
                  data-testid="auth-email"
                  onFocus={(e) => e.target.style.borderColor = colors.borderFocus}
                  onBlur={(e) => e.target.style.borderColor = colors.border}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  color: colors.textSecondary,
                  fontSize: '0.875rem',
                  marginBottom: '8px',
                  fontWeight: '500',
                }}>Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '14px 48px 14px 16px',
                      backgroundColor: colors.bgInput,
                      border: `2px solid ${colors.border}`,
                      borderRadius: '12px',
                      color: colors.textPrimary,
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'all 0.2s ease',
                      boxSizing: 'border-box',
                    }}
                    placeholder={isLogin && !inviteData ? (isBM ? "Enter password" : "Enter password") : (isBM ? "Min. 8 characters" : "Min. 8 characters")}
                    required
                    data-testid="auth-password"
                    onFocus={(e) => e.target.style.borderColor = colors.borderFocus}
                    onBlur={(e) => e.target.style.borderColor = colors.border}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      color: colors.textMuted,
                      cursor: 'pointer',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {showPassword ? <EyeOff style={{ width: '20px', height: '20px' }} /> : <Eye style={{ width: '20px', height: '20px' }} />}
                  </button>
                </div>
                
                {/* Password Strength Indicator - Only show on register */}
                {(!isLogin || inviteData) && (
                  <div style={{ marginTop: '10px', padding: '12px', background: 'rgba(255,255,255,0.05)', borderRadius: '10px' }}>
                    <div style={{ fontSize: '11px', color: '#888', marginBottom: '8px', fontWeight: '600' }}>
                      📋 {isBM ? 'Keperluan Password:' : 'Password Requirements:'}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {[
                        { check: formData.password.length >= 8, label: isBM ? 'Minimum 8 aksara' : 'Minimum 8 characters' },
                        { check: /[A-Z]/.test(formData.password), label: isBM ? '1 huruf BESAR (A-Z)' : '1 uppercase (A-Z)' },
                        { check: /[a-z]/.test(formData.password), label: isBM ? '1 lowercase letter (a-z)' : '1 lowercase (a-z)' },
                        { check: /[0-9]/.test(formData.password), label: isBM ? '1 number (0-9)' : '1 number (0-9)' },
                        { check: /[!@#$%^&*(),.?":{}|<>]/.test(formData.password), label: isBM ? '1 symbol (!@#$%^&*)' : '1 symbol (!@#$%^&*)' }
                      ].map((item, i) => (
                        <div key={i} style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '8px',
                          fontSize: '12px',
                          color: item.check ? '#4ADE80' : (formData.password ? '#FF6B6B' : '#666')
                        }}>
                          <span style={{ width: '16px', textAlign: 'center' }}>
                            {item.check ? '✓' : (formData.password ? '✗' : '○')}
                          </span>
                          <span>{item.label}</span>
                        </div>
                      ))}
                    </div>
                    {formData.password && (
                      <div style={{ 
                        marginTop: '10px', 
                        paddingTop: '10px', 
                        borderTop: '1px solid rgba(255,255,255,0.1)',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {(() => {
                          const checks = [
                            formData.password.length >= 8,
                            /[A-Z]/.test(formData.password),
                            /[a-z]/.test(formData.password),
                            /[0-9]/.test(formData.password),
                            /[!@#$%^&*(),.?":{}|<>]/.test(formData.password)
                          ];
                          const passed = checks.filter(Boolean).length;
                          if (passed === 5) return <span style={{ color: '#4ADE80' }}>✅ {isBM ? 'Password memenuhi semua syarat!' : 'Password meets all requirements!'}</span>;
                          return <span style={{ color: '#FF6B6B' }}>⚠️ {isBM ? `Sila lengkapkan ${5 - passed} syarat lagi` : `Please complete ${5 - passed} more requirements`}</span>;
                        })()}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {(!isLogin || inviteData) && (
                <div>
                  <label style={{
                    display: 'block',
                    color: colors.textSecondary,
                    fontSize: '0.875rem',
                    marginBottom: '8px',
                    fontWeight: '500',
                  }}>{isBM ? 'No. Telefon (Opsional)' : 'Phone Number (Optional)'}</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      backgroundColor: colors.bgInput,
                      border: `2px solid ${colors.border}`,
                      borderRadius: '12px',
                      color: colors.textPrimary,
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'all 0.2s ease',
                      boxSizing: 'border-box',
                    }}
                    placeholder="+60123456789"
                    data-testid="register-phone"
                    onFocus={(e) => e.target.style.borderColor = colors.borderFocus}
                    onBlur={(e) => e.target.style.borderColor = colors.border}
                  />
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                data-testid="auth-submit"
                style={{
                  width: '100%',
                  padding: '16px',
                  background: colors.accentGradient,
                  color: '#FFFFFF',
                  fontWeight: '600',
                  fontSize: '1rem',
                  borderRadius: '12px',
                  border: 'none',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease',
                  marginTop: '8px',
                  boxShadow: '0 4px 15px rgba(224, 30, 58, 0.4)',
                }}
              >
                {loading ? (
                  <div style={{
                    width: '20px',
                    height: '20px',
                    border: '2px solid rgba(255,255,255,0.3)',
                    borderTopColor: '#fff',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                  }} />
                ) : inviteData ? (
                  <>
                    <Gift style={{ width: '20px', height: '20px' }} />
                    {isBM ? 'Register with Invite' : 'Register with Invite'}
                  </>
                ) : isLogin ? (
                  <>
                    <LogIn style={{ width: '20px', height: '20px' }} />
                    {isBM ? 'Login' : 'Login'}
                  </>
                ) : (
                  <>
                    <UserPlus style={{ width: '20px', height: '20px' }} />
                    {isBM ? 'Daftar Sekarang' : 'Register Now'}
                  </>
                )}
              </button>

              {/* Forgot Password Link - Only show on login */}
              {isLogin && (
                <div style={{ textAlign: 'center', marginTop: '12px' }}>
                  <button
                    type="button"
                    onClick={() => { setShowForgotPassword(true); setError(''); setSuccess(''); }}
                    data-testid="forgot-password-link"
                    style={{
                      background: 'none',
                      border: 'none',
                      color: colors.textMuted,
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      padding: 0,
                      textDecoration: 'underline',
                    }}
                  >
                    {isBM ? 'Forgot password?' : 'Forgot password?'}
                  </button>
                </div>
              )}
            </form>
            )}

            {/* Bottom Text - Hide when invite is active or in forgot/reset mode */}
            {!inviteData && !showForgotPassword && !showResetPassword && (
              <p style={{
                textAlign: 'center',
                color: colors.textMuted,
                fontSize: '0.875rem',
                marginTop: '24px',
              }}>
                {isLogin ? (
                  <>
                    Don't have an account?{' '}
                    <button
                      onClick={() => setIsLogin(false)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: colors.accent,
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '0.875rem',
                        padding: 0,
                      }}
                    >
                      Register now
                    </button>
                  </>
                ) : (
                  <>
                    Already have an account?{' '}
                    <button
                      onClick={() => setIsLogin(true)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: colors.accent,
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '0.875rem',
                        padding: 0,
                      }}
                    >
                      Login
                    </button>
                  </>
                )}
              </p>
            )}
          </div>
        </div>

        {/* Features */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '20px',
          marginTop: '24px',
          flexWrap: 'wrap',
        }}>
          {features.map((feature, idx) => (
            <div key={idx} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              color: 'rgba(255,255,255,0.35)',
              fontSize: '0.75rem',
            }}>
              <feature.icon style={{ width: '14px', height: '14px', color: 'rgba(224,30,58,0.6)' }} />
              <span>{feature.text}</span>
            </div>
          ))}
        </div>

        {/* Trusted Badge */}
        <div style={{
          textAlign: 'center',
          marginTop: '16px',
          padding: '10px 0',
          borderTop: '1px solid rgba(255,255,255,0.04)',
        }}>
          <div style={{
            fontSize: '0.65rem',
            color: 'rgba(255,255,255,0.2)',
            letterSpacing: '1px',
            textTransform: 'uppercase',
            marginBottom: '6px',
          }}>
            Trusted by Malaysian Traders
          </div>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '12px',
            alignItems: 'center',
          }}>
            {['Secure', 'Verified', 'Licensed'].map((badge, i) => (
              <div key={i} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '9px',
                color: 'rgba(255,255,255,0.25)',
              }}>
                <div style={{
                  width: '4px', height: '4px',
                  borderRadius: '50%',
                  background: 'rgba(224, 30, 58, 0.5)',
                }} />
                {badge}
              </div>
            ))}
          </div>
        </div>

        {/* Powered by Underads */}
        <div style={{
          textAlign: 'center',
          marginTop: '12px',
          fontSize: '0.7rem',
          color: 'rgba(255,255,255,0.2)',
          letterSpacing: '0.5px'
        }}>
          Powered by <span style={{ fontWeight: '600', color: 'rgba(255,255,255,0.35)' }}>Underads</span>
        </div>
      </div>

      {/* Keyframe animation for spinner */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        input::placeholder {
          color: #666666;
        }
      `}</style>
    </div>
  );
};

export default AuthPage;
